import {
    DocumentDuplicateIcon,
    PlusIcon,
    TrashIcon,
} from "@heroicons/react/24/solid";
import { useEffect, useState } from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { useNavigate } from "react-router-dom";
import { getServiceTasksByJob } from "../../services/serviceTasks/getServiceTasksByJob";
import DialogConfirmation from "../../components/Modals/DialogConfirmation";
import { cloneTaskWithChecklistItems } from "../../services/serviceTasks/cloneTaskWithChecklistItems";
import toast from "react-hot-toast";
import ReactTooltip from "react-tooltip";
import { getClientSubscriptionsByJobId } from "../../services/clientService/getClientSubscriptionsByJobId";
import { updateTasksOrder } from "../../services/serviceTasks/updateTasksOrder";
import { deleteTaskV2 } from "../../services/serviceTasks/deleteTaskV2";
import { ArrowsUpDownIcon } from "@heroicons/react/24/outline";
import TypeBadge from "../../components/TypeBadge";
import { term } from "../../terms";
import { usePermissions } from "../../context/PermissionsContext";

const EditTasksFromJob = ({ title, jobId }) => {
    const { can } = usePermissions();
    const navigate = useNavigate();
    const [tasks, setTasks] = useState([]);
    const [showDialogForDuplicateJob, setShowDialogForDuplicateJob] =
        useState(false);
    const [jobIdToClone, setJobIdToClone] = useState(null);
    const [jobIdToDelete, setJobIdToDelete] = useState("");
    const [showDialogForMovingJob, setShowDialogForMovingJob] = useState(false);
    const [showDialog, setShowDialog] = useState(false);
    const [extraContent, setExtraContent] = useState(null);
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const noPermissionMessage = "You don't have permission to do this.";
    const canReadPipelines =
        can("pipelines.read") || can("pipelines.write") || can("pipelines.delete");
    const canWritePipelines = can("pipelines.write");
    const canDeletePipelines = can("pipelines.delete");

    const getTasksFromAPI = async () => {
        if (!canReadPipelines) {
            setTasks([]);
            return;
        }
        const response = await getServiceTasksByJob(jobId);
        if (response.success) {
            setTasks(response.data);
            return;
        }
        setTasks([]);
    };

    useEffect(() => {
        getTasksFromAPI();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jobId, canReadPipelines]);

    const hasWritePermission = () => {
        if (canWritePipelines) return true;
        toast.error(noPermissionMessage);
        return false;
    };

    useEffect(() => {
        if (loading) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "auto";
        }

        return () => {
            document.body.style.overflow = "auto";
        };
    }, [loading]);

    /* const removeTask = async (task_id) => {
    const res = await deleteTaskV2(jobId, task_id);
    alert(res.data.message);
    if (res.data.success) {
      const tasks = await getServiceTasksByJob(jobId);
      setTasks(tasks.data);
    }
  }; */

    const validateSubscriptionsAffected = async () => {
        const response = await getClientSubscriptionsByJobId(jobId);
        if (response.data.success) {
            setExtraContent(response.data.clientServiceSubscriptions);
        } else {
            toast.error(response.data.message);
        }
    };

    const reorder = (list, startIndex, endIndex) => {
        const result = Array.from(list);
        const [removed] = result.splice(startIndex, 1);
        result.splice(endIndex, 0, removed);
        return result;
    };

    const onDragEnd = async (result) => {
        if (!hasWritePermission() || !result.destination) {
            return;
        }
        // dropped outside the list
        validateSubscriptionsAffected();
        setResult(result);
        setShowDialogForMovingJob(true);

        /* if (!result.destination) {
      return;
    }
    const items = reorder(tasks, result.source.index, result.destination.index);
    setTasks(items);
    console.log(items);
    //update tasks in the database
    await Promise.all(
      items.map(async (item, index) => {
        await updateTaskV2({
          jobId: jobId,
          taskId: item.id,
          name: item.name,
          order: index,
        });
      })
    ); */
    };

    const onClickCloneJob = async (jobIdToClone) => {
        if (!hasWritePermission()) {
            return;
        }

        setJobIdToClone(jobIdToClone);
        setShowDialogForDuplicateJob(true);
        const response = await getClientSubscriptionsByJobId(jobId);
        if (response.data.success) {
            setExtraContent(response.data.clientServiceSubscriptions);
        } else {
            toast.error(response.data.message);
        }
    };

    const onDeleteJob = async () => {
        if (!canDeletePipelines) {
            toast.error(noPermissionMessage);
            return;
        }

        const response = await deleteTaskV2(jobId, jobIdToDelete);
        console.log(response);
        if (response.data.success) {
            toast.success(response.data.message);
            const tasks = await getServiceTasksByJob(jobId);
            setTasks(tasks.data);
        } else {
            toast.error(response.data.message);
        }
        setShowDialog(false);
    };

    const onConfirmJobToDuplicate = async () => {
        const response = await cloneTaskWithChecklistItems(jobIdToClone);
        console.log(response);
        if (response.data.success) {
            toast.success(response.data.message);
        } else {
            toast.error(response.data.message);
        }
        setShowDialogForDuplicateJob(false);
        getTasksFromAPI();
    };

    const onConfirmMoveJob = async () => {
        const { source, destination } = result;

        if (!destination || source.index === destination.index) {
            setShowDialogForMovingJob(false);
            return;
        }

        const updatedTasks = reorder(tasks, source.index, destination.index);
        setTasks(updatedTasks);

        setLoading(true);

        try {
            const tasksOrder = updatedTasks.map((task, index) => ({
                taskId: task.id,
                order: index + 1,
            }));
            const response = await updateTasksOrder(jobId, tasksOrder);
            const isSuccess =
                typeof response.data?.success === "boolean"
                    ? response.data.success
                    : response.success;

            if (isSuccess) {
                toast.success(response.data?.message || "Tasks reordered successfully");
            } else {
                toast.error(response.data?.message || response.message || "Failed to reorder tasks");
            }
        } catch (err) {
            console.error("Error updating task order:", err);
            toast.error("Failed to reorder tasks");
        } finally {
            await getTasksFromAPI();
            setShowDialogForMovingJob(false);
            setLoading(false);
        }
    };

    const onClickDeleteJob = async (jobId) => {
        if (!canDeletePipelines) {
            toast.error(noPermissionMessage);
            return;
        }

        validateSubscriptionsAffected();
        setShowDialog(true);
        setJobIdToDelete(jobId);
    };

    return (
        <>
            {loading && (
                <div className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-white/80 pointer-events-auto">
                    <div className="w-16 h-16 mb-4 border-4 border-teal-500 rounded-full border-t-transparent animate-spin"></div>
                    <p className="text-lg font-medium text-gray-700">
                        Updating, please wait this could take a few seconds...
                    </p>
                </div>
            )}
            <div className="relative w-full mt-12 bg-white border rounded-sm shadow-lg border-slate-200">
                <ReactTooltip
                    type="light"
                    effect="solid"
                    place="top"
                    border={true}
                    borderColor={"rgb(220,220,220)"}
                    className="rounded-md bg-gray-50"
                    multiline={false}
                />
                <DialogConfirmation
                    showDialog={showDialogForDuplicateJob}
                    setShowDialog={showDialogForDuplicateJob}
                    onCancel={() => setShowDialogForDuplicateJob(false)}
                    onConfirm={() => onConfirmJobToDuplicate()}
                    title={"Are you sure you want to clone this item?"}
                    description={`The ${term("pipeline_singular")} its checklist items will be copied too.`}
                />
                <DialogConfirmation
                    showDialog={showDialogForMovingJob}
                    setShowDialog={setShowDialogForMovingJob}
                    onCancel={() => setShowDialogForMovingJob(false)}
                    onConfirm={() => onConfirmMoveJob()}
                    title={"Are you sure you want to move this item?"}
                    extraContent={extraContent}
                    disabled={loading}
                    loading={loading}
                />
                <DialogConfirmation
                    showDialog={showDialog}
                    setShowDialog={setShowDialog}
                    onCancel={() => setShowDialog(false)}
                    onConfirm={() => onDeleteJob()}
                    title={"Are you sure you want to delete this item?"}
                    extraContent={extraContent}
                />
                <header className="flex items-center justify-between w-full px-5 py-4 border-b border-slate-200">
                    <h2 className="font-semibold text-slate-800">
                        <TypeBadge type={`${term("step_plural")}`} />
                    </h2>
                    <span
                        data-tip={
                            canWritePipelines
                                ? `Add ${term("step_singular")}`
                                : noPermissionMessage
                        }
                        className="inline-flex"
                    >
                        <button
                            onClick={() =>
                                navigate(`/templates/job-templates/${jobId}/jobs/create`)
                            }
                            type="button"
                            disabled={loading || !canWritePipelines}
                            className={`inline-flex items-center p-1 text-white bg-teal-500 border border-transparent rounded-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${
                                loading || !canWritePipelines
                                    ? "opacity-50 cursor-not-allowed"
                                    : "hover:bg-teal-600"
                            }`}
                        >
                            <PlusIcon className="w-5 h-5" />
                        </button>
                    </span>
                </header>

                <div className="px-5 py-4 space-y-5">
                    <DragDropContext onDragEnd={onDragEnd}>
                        <Droppable droppableId="droppable">
                            {(provided, snapshot) => (
                                <div
                                    {...provided.droppableProps}
                                    ref={provided.innerRef}
                                >
                                    {tasks &&
                                        tasks.map((task, index) => {
                                            return (
                                                <Draggable
                                                    key={task.id}
                                                    draggableId={task.id}
                                                    index={index}
                                                    isDragDisabled={
                                                        loading || !canWritePipelines
                                                    }
                                                >
                                                    {(provided, snapshot) => (
                                                        <div
                                                            ref={provided.innerRef}
                                                            {...provided.draggableProps}
                                                            {...provided.dragHandleProps}
                                                        >
                                                            <div
                                                                key={index}
                                                                className="relative flex items-center p-2 my-4 bg-gray-200 border rounded-sm border-slate-300"
                                                            >
                                                                <div className="flex items-center justify-between w-full text-sm">
                                                                    <ArrowsUpDownIcon className="w-4 h-4 mx-2" />
                                                                    <label
                                                                        onClick={() =>
                                                                            navigate(
                                                                                `/templates/jobs/${jobId}/task/${task.id}`,
                                                                            )
                                                                        }
                                                                        htmlFor="comments"
                                                                        className="w-full p-1 font-medium text-gray-700 cursor-pointer hover:text-teal-500"
                                                                    >
                                                                        {task.name}
                                                                    </label>
                                                                    {/*  <button onClick={() => removeTask(task.id)}>
                                  <TrashIcon className="p-1 w-7 h-7 hover:text-red-500" />
                                </button> */}
                                                                    <button
                                                                        onClick={() =>
                                                                            onClickCloneJob(
                                                                                task.id,
                                                                            )
                                                                        }
                                                                        className="mx-2"
                                                                        disabled={
                                                                            loading ||
                                                                            !canWritePipelines
                                                                        }
                                                                        data-tip={
                                                                            canWritePipelines
                                                                                ? "Clone"
                                                                                : noPermissionMessage
                                                                        }
                                                                    >
                                                                        <DocumentDuplicateIcon className="w-5 h-5" />
                                                                    </button>
                                                                    <button
                                                                        onClick={() =>
                                                                            onClickDeleteJob(
                                                                                task.id,
                                                                            )
                                                                        }
                                                                        className="mx-2"
                                                                        disabled={
                                                                            loading ||
                                                                            !canDeletePipelines
                                                                        }
                                                                        data-tip={
                                                                            canDeletePipelines
                                                                                ? "Delete"
                                                                                : noPermissionMessage
                                                                        }
                                                                    >
                                                                        <TrashIcon className="w-5 h-5 text-red-500" />
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </Draggable>
                                            );
                                        })}
                                    {provided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    </DragDropContext>
                </div>
            </div>
        </>
    );
};

export default EditTasksFromJob;
